import type { MatrixEvent } from "matrix-js-sdk";
import type { MatrixCall } from "matrix-js-sdk/lib/webrtc/call";
import { isValidCallRingingContent, MATRIX_CALL_RINGING_EVENT_TYPE } from "./callRinging";
import { isValidCallEscalationContent, MATRIX_CALL_ESCALATE_EVENT_TYPE } from "./callEscalation";

const CALL_ANSWER_EVENT_TYPE = "m.call.answer";
const CALL_CANDIDATES_EVENT_TYPE = "m.call.candidates";

type FastLaneCall = Pick<
    MatrixCall,
    "callId" | "hasPeerConnection" | "onAnswerReceived" | "onRemoteIceCandidatesReceived"
>;

interface ActiveCallEventFastLaneDependencies {
    readonly decryptEvent: (event: MatrixEvent) => Promise<void>;
    readonly findActiveCall: (callId: string) => FastLaneCall | undefined;
    readonly getBufferedEvents: () => MatrixEvent[] | undefined;
    readonly hasActiveCallInRoom: (roomId: string | undefined) => boolean;
    readonly isOwnEvent: (event: MatrixEvent) => boolean;
    readonly notifyVoipEventReceived: (event: MatrixEvent) => void;
    readonly notifyCallRinging: (event: MatrixEvent) => void;
    readonly notifyCallEscalation?: (event: MatrixEvent) => void;
}

export class ActiveCallEventFastLane {
    private processingChain: Promise<void> = Promise.resolve();

    public constructor(private readonly dependencies: ActiveCallEventFastLaneDependencies) {}

    public handle(event: MatrixEvent): Promise<boolean> {
        const processing = this.processingChain.then(() => this.tryHandle(event));
        this.processingChain = processing.then(
            () => undefined,
            () => undefined,
        );
        return processing;
    }

    private async tryHandle(event: MatrixEvent): Promise<boolean> {
        if (!this.dependencies.hasActiveCallInRoom(event.getRoomId() ?? undefined)) {
            return false;
        }

        await this.dependencies.decryptEvent(event);

        const eventType = event.getType();
        if (eventType === MATRIX_CALL_RINGING_EVENT_TYPE) {
            return this.handleRinging(event);
        }
        if (eventType === MATRIX_CALL_ESCALATE_EVENT_TYPE) {
            return this.handleEscalation(event);
        }
        if (eventType !== CALL_ANSWER_EVENT_TYPE && eventType !== CALL_CANDIDATES_EVENT_TYPE) {
            return false;
        }

        if (this.dependencies.isOwnEvent(event)) {
            return false;
        }

        const content = event.getContent();
        const callId = typeof content.call_id === "string" ? content.call_id : undefined;
        if (!callId) {
            return false;
        }

        const call = this.dependencies.findActiveCall(callId);
        if (!call?.hasPeerConnection) {
            return false;
        }

        const bufferedEvents = this.dependencies.getBufferedEvents();
        const eventIndex = bufferedEvents?.indexOf(event) ?? -1;
        if (!bufferedEvents || eventIndex < 0) {
            return false;
        }

        bufferedEvents.splice(eventIndex, 1);

        try {
            this.dependencies.notifyVoipEventReceived(event);
            if (eventType === CALL_ANSWER_EVENT_TYPE) {
                await call.onAnswerReceived(event);
            } else {
                await call.onRemoteIceCandidatesReceived(event);
            }
            return true;
        } catch (error) {
            const currentBuffer = this.dependencies.getBufferedEvents();
            if (currentBuffer && !currentBuffer.includes(event)) {
                if (currentBuffer === bufferedEvents) {
                    currentBuffer.splice(Math.min(eventIndex, currentBuffer.length), 0, event);
                } else {
                    currentBuffer.push(event);
                }
            }
            throw error;
        }
    }

    private handleRinging(event: MatrixEvent): boolean {
        if (this.dependencies.isOwnEvent(event)) {
            return false;
        }

        const content = event.getContent();
        if (!isValidCallRingingContent(content) || !this.dependencies.findActiveCall(content.call_id)) {
            return false;
        }

        this.dependencies.notifyCallRinging(event);
        return true;
    }

    private handleEscalation(event: MatrixEvent): boolean {
        if (this.dependencies.isOwnEvent(event) || !this.dependencies.notifyCallEscalation) {
            return false;
        }

        const content = event.getContent();
        if (!isValidCallEscalationContent(content) || !this.dependencies.findActiveCall(content.call_id)) {
            return false;
        }

        this.dependencies.notifyCallEscalation(event);
        return true;
    }
}
